import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

/**
 * EyesButton — floating 👁 button on the orb screen. Tap it → captures one
 * frame from the laptop webcam (Phase 1) or the latest ESP32-S3 frame
 * (Phase 2, when uploaded), asks GPT-5.2 Vision the supplied question, and
 * speaks the reply via ElevenLabs.
 *
 * Owner asked: "Can I link [the camera] to my agents so they can see what's
 * in front of me?" — this is the entry point for that.
 *
 * Modes:
 *   - "webcam": uses navigator.mediaDevices.getUserMedia (Phase 1, default)
 *   - "esp32":  pulls the latest pushed frame from /jarvis/camera/latest
 */
const PROMPT_DEFAULT = "What do you see? Describe it briefly and tell me what to do next.";

export default function EyesButton({ onSpeak }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [reply, setReply] = useState("");
  const [question, setQuestion] = useState(PROMPT_DEFAULT);
  // Default to ESP32 mode because the rig has no laptop webcam — the
  // primary "eye" is the ESP32-S3 Wi-Fi camera that POSTs frames to
  // /api/jarvis/camera/upload. Choice is persisted across reloads.
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem("rawco-eyes-mode") || "esp32"; }
    catch { return "esp32"; }
  });
  const [error, setError] = useState("");
  const [watching, setWatching] = useState(false);
  const [lastAlert, setLastAlert] = useState("");
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const lastFrameIdRef = useRef("");
  // ─── DRAGGABLE PANEL ─────────────────────────────────────────────
  // Owner asked: "the eyes popup box won't let me move it — that needs
  // to be movable." Position is stored in localStorage so the panel
  // remembers where the owner dragged it last session.
  const [panelPos, setPanelPos] = useState(() => {
    try {
      const raw = localStorage.getItem("rawco-eyes-panel-pos");
      if (raw) {
        const p = JSON.parse(raw);
        if (typeof p.right === "number" && typeof p.bottom === "number") return p;
      }
    } catch { /* ignore */ }
    return { right: 24, bottom: 24 };
  });
  const dragRef = useRef(null);
  const startDrag = (e) => {
    if (e.button !== 0 && e.type !== "touchstart") return;
    const point = e.touches ? e.touches[0] : e;
    dragRef.current = {
      startX: point.clientX,
      startY: point.clientY,
      startRight: panelPos.right,
      startBottom: panelPos.bottom,
    };
    const onMove = (ev) => {
      if (!dragRef.current) return;
      const p = ev.touches ? ev.touches[0] : ev;
      const dx = p.clientX - dragRef.current.startX;
      const dy = p.clientY - dragRef.current.startY;
      const nextRight = Math.max(0, dragRef.current.startRight - dx);
      const nextBottom = Math.max(0, dragRef.current.startBottom - dy);
      setPanelPos({ right: nextRight, bottom: nextBottom });
    };
    const onUp = () => {
      try {
        if (dragRef.current) {
          // Persist FINAL position once mouse-up; avoids hammering localStorage
          // every mousemove tick.
          localStorage.setItem("rawco-eyes-panel-pos", JSON.stringify(panelPos));
        }
      } catch { /* ignore */ }
      dragRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  };

  // Keep-watching mode — every 60s pull the latest ESP32 frame through the
  // /jarvis/see/watch endpoint. The server returns NONE for boring frames
  // (so we don't speak constantly); only genuine new events get spoken.
  // Toggled off automatically if the panel closes.
  useEffect(() => {
    if (!watching) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      // 🛡 Don't speak over the user/main brain — owner complaint: "it keeps
      // talking over me and explaining every 3 seconds, doesn't finish
      // sentences". Skip the poll entirely if Jarvis is speaking or
      // thinking. Also respects a global kill-switch the voice command
      // "turn the eyes off" sets on the window.
      // eslint-disable-next-line no-undef
      if (window.__rcoEyesMuted) return;
      try {
        const r = await api.post("/jarvis/see/watch", {
          last_alert_frame_id: lastFrameIdRef.current,
        });
        if (cancelled) return;
        if (r.data?.alert) {
          lastFrameIdRef.current = r.data.frame_id || "";
          setLastAlert(r.data.alert);
          // Only narrate if nothing else is speaking — prevents the
          // overlapping-voice loop.
          if (!window.__rcoJarvisSpeaking) onSpeak?.(r.data.alert);
        }
      } catch { /* swallow polling errors */ }
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [watching, onSpeak]);

  // Persist mode + auto-fall back to ESP32 if the laptop reports zero
  // cameras (the user's setup has no built-in webcam — the eye is the
  // ESP32-S3 Wi-Fi camera). This prevents the "No camera available"
  // dead-end the browser camera settings panel shows.
  useEffect(() => {
    try { localStorage.setItem("rawco-eyes-mode", mode); } catch { /* ignore */ }
    if (mode !== "webcam" || !navigator.mediaDevices?.enumerateDevices) return;
    (async () => {
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const cams = devs.filter(d => d.kind === "videoinput");
        if (cams.length === 0) {
          setMode("esp32");
          setError("No local webcam found — switched to ESP32-S3 camera.");
        }
      } catch { /* ignore enumeration failures */ }
    })();
  }, [mode]);

  // Start/stop the webcam feed when the panel opens in webcam mode.
  useEffect(() => {
    if (!open || mode !== "webcam") {
      stopStream();
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        // Permission probe — surfaces a clear "permission denied" message
        // instead of a silent auto-flip to ESP32 (owner brief Feb 2026:
        // "My eyes camera was working now it's just a white screen?").
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Browser does not support camera API (use HTTPS + a modern browser).");
        }
        const s = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment", width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (cancelled) { s.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = s;
        if (videoRef.current) {
          videoRef.current.srcObject = s;
          // Defensive .play() — some browsers ignore the autoPlay attribute
          // if the video element mounts BEFORE the stream is attached.
          try { await videoRef.current.play(); } catch { /* autoPlay handles it */ }
        }
        setError("");
      } catch (e) {
        // Surface a USEFUL diagnostic instead of silently flipping. Owner
        // sees the actual reason and can fix it (allow permission, close
        // the other app holding the cam, or hit ESP32 manually).
        const name = e?.name || "";
        const msg =
          name === "NotAllowedError" ? "Camera permission denied — click the camera icon in the browser address bar and allow access, then hit Retry." :
          name === "NotFoundError"   ? "No camera detected on this device. Switch to ESP32 mode below." :
          name === "NotReadableError"? "Camera is in use by another app (Zoom/Teams/another browser tab). Close it and hit Retry." :
          name === "OverconstrainedError" ? "Camera doesn't support the requested resolution. Hit Retry — we'll try lower." :
          (e?.message || "Camera failed to start.");
        if (!cancelled) setError(msg);
        // No more silent auto-flip — the owner stays in webcam mode and
        // either retries or manually taps ESP32.
      }
    })();
    return () => { cancelled = true; stopStream(); };
  }, [open, mode]);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  // DOM mutation on refs (canvas size + video srcObject) is the standard
  // pattern for these APIs — the file-level disable above silences the
  // React Compiler immutability rule.
  const captureWebcamFrame = () => {
    const v = videoRef.current;
    const c = canvasRef.current;
    if (!v || !c) return null;
    const w = v.videoWidth || 640;
    const h = v.videoHeight || 480;
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    const dataUrl = c.toDataURL("image/jpeg", 0.85);
    return dataUrl;
  };

  const ask = async () => {
    if (busy) return;
    setBusy(true);
    setReply("");
    setError("");
    try {
      let img = null;
      if (mode === "webcam") {
        img = captureWebcamFrame();
        if (!img) { setError("Couldn't capture a frame from the webcam."); return; }
        setPreviewUrl(img);
      } else {
        // ESP32 mode — fetch the latest pushed frame URL preview
        try {
          const r = await api.get("/jarvis/camera/latest");
          if (r.data?.image_base64) {
            img = `data:image/jpeg;base64,${r.data.image_base64}`;
            setPreviewUrl(img);
          } else {
            setError("No ESP32 frame uploaded yet. Flash the sketch first.");
            return;
          }
        } catch (e) {
          setError(`No ESP32 frame available: ${e.response?.data?.detail || e.message}`);
          return;
        }
      }
      const r = await api.post("/jarvis/see", {
        question: question || PROMPT_DEFAULT,
        image_base64: img,
      });
      const text = r.data?.reply || "I couldn't see anything in that frame.";
      setReply(text);
      onSpeak?.(text);
    } catch (e) {
      setError(`Vision call failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        data-testid="eyes-button"
        title="Show RAW-DATA what's in front of you"
        className="fixed z-[30] pointer-events-auto"
        style={{
          right: "calc(50% - min(35vw, 280px) - 110px)",
          top: "calc(50% + 72px)",
          background: "rgba(0,0,0,0.65)",
          border: `1px solid ${open ? "var(--rc-gold)" : "var(--rc-gold-dim)"}`,
          color: "var(--rc-gold)",
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: "10px",
          letterSpacing: "0.16em",
          padding: "10px 12px",
          backdropFilter: "blur(4px)",
          boxShadow: open ? "0 0 18px rgba(255,184,0,0.4)" : "0 0 10px rgba(255,184,0,0.2)",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        <div style={{ fontSize: 18, marginBottom: 4, lineHeight: 1 }}>👁</div>
        <div>EYES</div>
      </button>

      {open && (
        <div
          className="fixed z-[40] pointer-events-auto"
          data-testid="eyes-panel"
          style={{
            right: panelPos.right, bottom: panelPos.bottom,
            width: 380,
            background: "rgba(0, 4, 12, 0.96)",
            border: "1px solid var(--rc-gold)",
            boxShadow: "0 0 30px rgba(255,184,0,0.25)",
            fontFamily: "'JetBrains Mono', monospace",
            color: "var(--rc-text)",
          }}
        >
          <div
            className="flex items-center justify-between p-3 border-b border-[var(--rc-gold-dim)]"
            onMouseDown={startDrag}
            onTouchStart={startDrag}
            style={{ cursor: "move", userSelect: "none" }}
            data-testid="eyes-panel-drag-handle"
          >
            <div>
              <div className="font-display rc-glow-text text-sm">// EYES</div>
              <div className="text-[10px] opacity-60">{mode === "webcam" ? "Using laptop webcam" : "Using ESP32-S3 camera"}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setMode(m => m === "webcam" ? "esp32" : "webcam")}
                title="Switch source"
                data-testid="eyes-mode-toggle"
                className="text-[9px] tracking-widest border border-[var(--rc-gold-dim)] hover:border-[var(--rc-gold)] px-2 py-1"
              >{mode === "webcam" ? "ESP32" : "WEBCAM"}</button>
              <button onClick={() => setOpen(false)} className="text-[var(--rc-gold)]" data-testid="eyes-close">✕</button>
            </div>
          </div>

          <div className="p-3 space-y-2">
            <div className="aspect-video w-full bg-black border border-[var(--rc-gold-dim)] overflow-hidden flex items-center justify-center">
              {mode === "webcam" ? (
                <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" data-testid="eyes-video" style={{ background: "#000" }} />
              ) : previewUrl ? (
                <img src={previewUrl} alt="ESP32 frame" className="w-full h-full object-cover" />
              ) : (
                <div className="text-[10px] opacity-60 p-3 text-center">No ESP32 frame yet. Flash the sketch and wait for the first upload.</div>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              rows={2}
              placeholder="What should I look for?"
              data-testid="eyes-question"
              className="w-full bg-transparent border border-[var(--rc-gold-dim)] focus:border-[var(--rc-gold)] outline-none px-2 py-1.5 text-xs resize-none"
            />

            <button
              onClick={ask}
              disabled={busy}
              data-testid="eyes-ask"
              className="rc-btn w-full !text-[11px] !py-2"
            >{busy ? "// LOOKING…" : "👁 ASK MY EYES"}</button>

            {/* Keep-watching toggle — passive mode. Only works in ESP32 mode
                since the laptop webcam stream would have to stay on. */}
            <label className="flex items-center justify-between border border-[var(--rc-gold-dim)] px-2 py-1.5 text-[10px] cursor-pointer"
                   data-testid="eyes-watch-label">
              <span>
                <span className="rc-label opacity-80">KEEP WATCHING</span>
                <span className="opacity-50 ml-2">{mode === "esp32" ? "(polls every 60s)" : "(needs ESP32 mode)"}</span>
              </span>
              <input
                type="checkbox"
                checked={watching}
                disabled={mode !== "esp32"}
                onChange={(e) => setWatching(e.target.checked)}
                data-testid="eyes-watch-toggle"
                className="accent-[var(--rc-gold)]"
              />
            </label>

            {lastAlert && watching && (
              <div className="border border-[var(--rc-gold)] p-2 text-[11px] bg-[var(--rc-gold-dim)] bg-opacity-10" data-testid="eyes-watch-alert">
                <div className="rc-label text-[9px] opacity-80 mb-1">📣 PROACTIVE ALERT</div>
                {lastAlert}
              </div>
            )}

            {error && (
              <div className="text-[10px] text-red-400 border border-red-900 p-2 flex items-start gap-2" data-testid="eyes-error">
                <span className="flex-1">{error}</span>
                <button
                  type="button"
                  onClick={() => {
                    // Force the effect to re-run: flip mode away and back.
                    setError("");
                    setMode((m) => m); // no-op set still triggers useEffect re-eval
                    // Trick: toggle mode to esp32 then back to webcam to force a re-attempt.
                    setMode("esp32");
                    setTimeout(() => setMode("webcam"), 50);
                  }}
                  data-testid="eyes-retry"
                  className="text-[9px] tracking-widest border border-red-400 px-2 py-0.5 hover:bg-red-400 hover:text-black transition-all"
                >RETRY</button>
              </div>
            )}

            {reply && (
              <div className="border border-[var(--rc-gold-dim)] p-2 text-[11px] whitespace-pre-wrap" data-testid="eyes-reply">
                <div className="rc-label text-[9px] opacity-70 mb-1">RAW-DATA SAYS</div>
                {reply}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
